import React, { useState } from "react";

import ShipeeziNewGrid, { Column } from "Components/AppGrid/shipeezi-new-grid";
import { SubGridContainer } from "Components/Generic/Grid/styles";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { Branch } from "Models/branch";
import { Member } from "Models/members/member";
import { Task } from "Models/task";
import { useTranslation } from "react-i18next";
import MoreOptionOrderComponent from "Components/MoreOptionOrderComponent/MoreOptionOrderComponent";
import { Fleet } from "Models/fleet";
import { getEquipmentName } from "Util/getEquipmentName";
import { Expense } from "Models/expenses";
import { useAppState } from "Context/StateContext";
import BadgeJobStatus from "Components/Generic/BadgeJobStatus";

interface TasksGridAfterRowProps {
  tasks: Task[];
  loading: boolean;
}

export const TasksGridAfterRow = ({ tasks, loading }: TasksGridAfterRowProps) => {
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);

  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const currency = currentUser?.company?.currency;

  const handleCurrentPage = (numPage: number) => {
    setPage(numPage - 1);
  };

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const formatDate = (date: string) => {
    if (date) {
      return format(
        utcToZonedTime(date, Intl.DateTimeFormat().resolvedOptions().timeZone),
        "dd/MM/yyyy hh:mm a"
      );
    }

    return "-";
  };

  const formatCurrency = (value: number) => {
    return `${currency?.symbol}${value.toFixed(Number(currency?.decimalPoints) || 2)}`;
  };

  const getColumns = () => {
    const columns: Column[] = [
      {
        name: "type",
        hideSearch: true,
        title: translate("taskType"),
      },
      {
        name: "branchFrom",
        hideSearch: true,
        title: translate("origin"),
        formatText: (branch: Branch) => branch?.name || "-",
      },
      {
        name: "members",
        hideSearch: true,
        hideSort: true,
        title: translate("members"),
        formatComponent: (members: Member[]) => {
          if (!members?.length) return "-";

          return (
            <MoreOptionOrderComponent
              moreOptionsTitle={String(members?.length)}
              options={members?.map((member) => member.fullName ?? [])}
            />
          );
        },
      },
      {
        name: "fleets",
        hideSearch: true,
        hideSort: true,
        title: translate("equipments"),
        formatComponent: (equipments: Fleet[]) => {
          if (!equipments?.length) return "-";

          return (
            <MoreOptionOrderComponent
              moreOptionsTitle={String(equipments?.length)}
              options={
                equipments?.map((equipment) => {
                  const { fullText } = getEquipmentName(
                    equipment,
                    translate("general"),
                    translate("fleet")
                  );

                  return fullText;
                }) || []
              }
            />
          );
        },
      },
      {
        name: "expenses",
        hideSearch: true,
        title: translate("others"),
        formatComponent: (others: Expense[]) => {
          const othersExpenses = others?.filter((expense) => !expense.fleetId && !expense.memberId);

          if (!othersExpenses?.length) return "-";

          return (
            <MoreOptionOrderComponent
              moreOptionsTitle={String(othersExpenses?.length)}
              options={othersExpenses?.map((other) => other.description ?? [])}
            />
          );
        },
      },
      {
        name: "startDate",
        title: translate("taskBooked"),
        hideSearch: true,
        formatComponent: formatDate,
      },
      {
        name: "updatedAt",
        title: translate("lastUpdated"),
        hideSearch: true,
        formatComponent: formatDate,
      },
      {
        name: "expenses",
        hideSearch: true,
        title: translate("totalCost"),
        formatComponent: (expenses: Expense[]) => {
          if (!expenses?.length) return "-";

          const totalCost = expenses.reduce((acc, expense) => {
            let cost = expense.totalCost || 0;

            return acc + cost;
          }, 0);

          return totalCost ? formatCurrency(totalCost) : "-";
        },
      },
      {
        name: "status",
        title: translate("status"),
        formatComponent: (status: string) => <BadgeJobStatus value={status} />,
        hideSearch: true,
      },
    ];

    return columns;
  };

  return (
    <SubGridContainer>
      <ShipeeziNewGrid
        loadingData={loading}
        data={tasks}
        onChangePageSize={handlePageSize}
        onNextPage={handleCurrentPage}
        onPreviousPage={handleCurrentPage}
        onGoToPage={handleCurrentPage}
        columns={getColumns()}
        totalItems={tasks?.length}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        positionActionsColumn="right"
        showSelection={false}
        showHeaderGrid={false}
        showFooterGrid={false}
        showFooterPageInfo={false}
        showSort={false}
        showColumnChooser={false}
      />
    </SubGridContainer>
  );
};
